import React, { ReactNode, useMemo, forwardRef, ElementType, ComponentType } from 'react';
import { Box, BoxProps, CSSObject, SvgIconProps, useTheme } from '@mui/material';
import { CircleExclamationIcon } from '@/src/components/icon/stroke/CircleExclamationIcon';
import { CircleInformationIcon } from '@/src/components/icon/stroke/CircleInformationIcon';
import { getFocusRingStyle } from '@/src/components/focusring/focusring';
import { getInteractiveStyle } from '@/src/materialui/button/interactive';

export interface LabelBaseProps extends BoxProps<ElementType> {
  /**
   * Content to be rendered inside Label, alternatively children are used
   */
  label?: ReactNode;
}

export interface GeneralLabelProps extends LabelBaseProps {
  type?: 'general';
}

export interface SimpleLabelProps extends LabelBaseProps {
  type: 'simple';
}

export type FeatureVariants =
  | 'primary'
  | 'neutral'
  | 'secondary'
  | 'discount'
  | 'alert'
  | 'error'
  | 'info'
  | 'success'
  | 'warning';
export interface FeatureLabelProps extends LabelBaseProps {
  type: 'feature';
  variant?: FeatureVariants;
}

export type SemanticVariants = 'positive' | 'info' | 'warning' | 'alert' | 'error';
export interface SemanticLabelProps extends LabelBaseProps {
  type: 'semantic';
  variant?: SemanticVariants;
  iconProps?: SvgIconProps;
  IconComponent?: ComponentType<SvgIconProps>;
}

export type StatusVariants = 'positive' | 'warning' | 'alert' | 'error' | 'neutral' | 'disabled';
export interface StatusLabelProps extends LabelBaseProps {
  type: 'status' | 'indicator';
  variant?: StatusVariants;
  dotProps?: BoxProps<ElementType>;
  labelPlacement?: 'end' | 'start' | 'top' | 'bottom';
}

export type CO2Variants = 'A+++' | 'A++' | 'A+' | 'A' | 'B' | 'C' | 'D' | 'E' | 'F' | 'G';
export interface CO2LabelProps extends LabelBaseProps {
  type: 'CO2';
  variant?: CO2Variants;
  size?: 'small' | 'large';
  labelBoxProps?: BoxProps<ElementType>;
  labelNextProps?: BoxProps<ElementType>;
}

export type LabelProps =
  | SimpleLabelProps
  | FeatureLabelProps
  | SemanticLabelProps
  | StatusLabelProps
  | CO2LabelProps
  | GeneralLabelProps;

// Shared style for all variants
const baseSx: CSSObject = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'flex-start',
  flexWrap: 'nowrap',
  flexShrink: '0',
  // font
  fontSize: '11px',
  lineHeight: '12px',
  letterSpacing: '3px',
  textTransform: 'uppercase',
  fontWeight: '900',
  whiteSpace: 'nowrap',
  textRendering: 'geometricPrecision',
};

const GeneralVariant: React.FC<GeneralLabelProps> = forwardRef(({ type: _, label, children, sx, ...rest }, ref) => (
  <Box
    ref={ref}
    data-testid="Label-general"
    sx={{
      ...baseSx,
      color: (theme) => (theme.palette.mode === 'dark' ? 'onSurface.os200' : 'onSurface.os600'),
      ...sx,
    }}
    {...rest}
  >
    {label || children}
  </Box>
));

const SimpleVariant: React.FC<SimpleLabelProps> = forwardRef(({ type: _, label, children, sx, ...rest }, ref) => (
  <Box
    ref={ref}
    data-testid="Label-simple"
    sx={(theme) => ({
      ...baseSx,
      ...theme.typography.list,
      backgroundColor: 'transparent.primary',
      color: 'text.primary',
      p: '2px 10px',
      ...sx,
    })}
    {...rest}
  >
    {label || children}
  </Box>
));

const FeatureVariant: React.FC<FeatureLabelProps> = forwardRef(
  ({ type: _, label, children, variant = 'primary', sx, ...rest }, ref) => (
    <Box
      ref={ref}
      data-testid={`Label-feature${variant}`}
      sx={{
        ...baseSx,
        color: 'onSurface.os0',
        backgroundColor: 'brand.tertiary',
        padding: '5px 7px 5px 10px',
        borderRadius: '2px',
        ...(variant === 'neutral' && {
          backgroundColor: 'onSurface.os600',
        }),
        ...(variant === 'discount' && {
          color: 'brand.secondary',
          backgroundColor: 'brand.tertiary',
        }),
        // dark mode
        ...(variant === 'secondary' && {
          backgroundColor: 'brand.secondary',
          color: 'brand.tertiary',
        }),
        // semantic labels
        ...(['alert', 'error'].includes(variant) && {
          backgroundColor: 'error.main',
        }),
        ...(variant === 'info' && {
          backgroundColor: 'info.main',
        }),
        ...(variant === 'warning' && {
          backgroundColor: 'warning.dark',
        }),
        ...(variant === 'success' && {
          backgroundColor: 'brand.primary',
        }),
        ...sx,
      }}
      {...rest}
    >
      {label || children}
    </Box>
  ),
);

/**
 * Wrapper around getInteractiveStyle() function to provide interactive style
 *  only for label that is actually a button
 */
function getLabelInteractiveStyle(defaultStyle: CSSObject, hoverStyle: CSSObject): CSSObject {
  return {
    ...defaultStyle,
    '&.MuiButtonBase-root': {
      ...getInteractiveStyle(defaultStyle, hoverStyle),
    },
  };
}

const SemanticVariant: React.FC<SemanticLabelProps> = forwardRef(
  ({ type: _, label, children, variant = 'positive', sx, iconProps, IconComponent, onClick, ...rest }, ref) => {
    const { sx: iconSx, ...restIconProps } = iconProps || {};
    const VariantIconComponent = useMemo(() => {
      switch (variant) {
        case 'alert':
        case 'error':
        case 'warning':
          return CircleExclamationIcon;
        case 'info':
        case 'positive':
        default:
          return CircleInformationIcon;
      }
    }, [variant]);
    const SemanticIcon = IconComponent || VariantIconComponent;
    const theme = useTheme();

    return (
      <Box
        ref={ref}
        data-testid={`Label-semantic${variant}`}
        sx={{
          ...baseSx,
          gap: '6px',
          padding: '2px 8px 2px 4px',
          borderRadius: '4px',
          border: 1,
          borderColor: 'currentColor',
          transition:
            'transform 0.3s ease-out, color 0.3s ease-out, background-color 0.3s ease-out, border-color 0.3s ease-out, box-shadow 0.3s ease-out, opacity 0.3s ease-out',
          // clickable style
          '&&.MuiButtonBase-root': {
            // focused style
            ...getFocusRingStyle(theme, true, 1),
          },
          // default, positive
          ...getLabelInteractiveStyle(
            {
              color: 'brand.primary',
              backgroundColor: 'transparent.transparent',
            },
            {
              color: 'brandHover.primary',
              backgroundColor: 'transparent.primary',
            },
          ),
          ...(['alert', 'error'].includes(variant) && {
            ...getLabelInteractiveStyle(
              {
                color: 'error.main',
                backgroundColor: 'transparent.transparent',
              },
              {
                color: 'error.dark',
                backgroundColor: 'transparent.primary',
              },
            ),
          }),
          ...(variant === 'warning' && {
            ...getLabelInteractiveStyle(
              {
                color: 'warning.dark',
                backgroundColor: 'transparent.transparent',
              },
              {
                color: 'warning.dark',
                backgroundColor: 'transparent.primary',
              },
            ),
          }),
          ...(variant === 'info' && {
            ...getLabelInteractiveStyle(
              {
                color: 'info.main',
                backgroundColor: 'transparent.transparent',
              },
              {
                color: 'info.dark',
                backgroundColor: 'transparent.primary',
              },
            ),
          }),
          ...sx,
        }}
        onClick={onClick}
        {...rest}
      >
        <SemanticIcon data-testid={`icon-${variant}`} sx={{ fontSize: '18px', ...iconSx }} {...restIconProps} />
        {label || children}
      </Box>
    );
  },
);

const StatusVariant: React.FC<StatusLabelProps> = forwardRef(
  ({ type, label, children, variant = 'positive', labelPlacement = 'end', dotProps, sx, ...rest }, ref) => {
    const { sx: dotSx, ...restDot } = dotProps || {};

    return (
      <Box
        ref={ref}
        data-testid={`Label-status${variant}`}
        sx={(theme) => ({
          ...baseSx,
          color: 'text.primary',
          flexDirection: { start: 'row-reverse', top: 'column-reverse', bottom: 'column' }[labelPlacement] ?? 'row',
          gap: 1,
          ...(type === 'indicator' && theme.typography.disclaimer1),
          ...sx,
        })}
        {...(variant === 'disabled' && {
          disabled: true,
          tabIndex: -1,
        })}
        {...rest}
      >
        <Box
          sx={{
            width: '10px',
            height: '10px',
            ...(type === 'indicator' && {
              width: '14px',
              height: '14px',
            }),
            borderRadius: '50%',
            backgroundColor: 'success.main',
            ...(variant === 'warning' && {
              backgroundColor: 'warning.dark',
            }),
            ...(['alert', 'error'].includes(variant) && {
              backgroundColor: 'error.main',
            }),
            ...(variant === 'neutral' && {
              backgroundColor: 'onSurface.os500',
            }),
            ...(variant === 'disabled' && {
              backgroundColor: 'disabled.secondary',
            }),
            ...dotSx,
          }}
          {...restDot}
        />
        {label || children}
      </Box>
    );
  },
);

const CO2Variant: React.FC<CO2LabelProps> = forwardRef(
  ({ type: _, label, children, variant = 'A+++', size = 'large', sx, labelBoxProps, labelNextProps, ...rest }, ref) => {
    const colorMap = useMemo(
      () => ({
        B: '#019A01',
        C: '#69CC00',
        D: '#FFCC01',
        E: '#F99601',
        F: '#FE6501',
        G: '#FF3100',
        H: '#2A78CD',
      }),
      [],
    );
    const getColor = (_variant: string) => colorMap[_variant] || '#016703';

    const { sx: labelBoxSx, ...restLabelBox } = labelBoxProps || {};
    const { sx: labelNextSx, ...restLabelNext } = labelNextProps || {};

    return (
      <Box
        ref={ref}
        data-testid={`Label-CO2${variant}`}
        sx={{
          ...baseSx,
          ...sx,
        }}
        {...rest}
      >
        <Box
          component="span"
          sx={{
            alignItems: 'center',
            backgroundColor: getColor(variant),
            boxSizing: 'border-box',
            color: 'surface.tertiary',
            display: 'inline-flex',
            justifyContent: 'flex-start',
            fontWeight: '700',
            height: { xs: '20px', xl: '22px' },
            fontSize: { xs: '12px', xl: '14px' },
            ...(size === 'small' && {
              fontSize: { xs: '10px', xl: '12px' },
              height: { xs: '16px', xl: '18px' },
            }),
            padding: '0 5px',
            position: 'relative',
            letterSpacing: '1px',
            ...labelBoxSx,
          }}
          {...restLabelBox}
        >
          {label || children || variant}
        </Box>
        <Box
          sx={{
            content: '""',
            display: 'block',
            width: '0',
            height: '0',
            borderStyle: 'solid',
            borderColor: `transparent transparent transparent ${getColor(variant)}`,
            borderWidth: {
              xs: `10px 0 10px 7px`,
              lg: `11px 0 11px 8px`,
            },
            ...(size === 'small' && {
              borderWidth: {
                xs: '8px 0 8px 5px',
                lg: '9px 0 9px 7px',
              },
            }),
            ...labelNextSx,
          }}
          {...restLabelNext}
        />
      </Box>
    );
  },
);

export const Label: React.FC<LabelProps> = forwardRef((props, ref) => {
  const LabelComponent = useMemo<React.FC<LabelProps>>(() => {
    switch (props.type) {
      case 'simple':
        return SimpleVariant;
      case 'feature':
        return FeatureVariant;
      case 'semantic':
        return SemanticVariant;
      case 'status':
      case 'indicator':
        return StatusVariant;
      case 'CO2':
        return CO2Variant;
      case 'general':
      default:
        return GeneralVariant;
    }
  }, [props.type]);

  return <LabelComponent component="span" {...props} ref={ref} />;
});
